import { recordEventWithCheck } from "src/apiService/modules/events";
import type { ClientErrorSource, EventError } from "src/interfaces/event";
import { getBrowserId } from "src/utils/browserId";

const DEDUP_WINDOW_MS = 60_000;
const MAX_REPORTS_PER_MINUTE = 10;

const recentKeys = new Map<string, number>();
let reportsThisMinute = 0;
let minuteWindowStart = Date.now();

export function isSupportPath(pathname: string): boolean {
  return pathname === "/support" || pathname.startsWith("/support/");
}

function shouldSkipReporting(): boolean {
  if (typeof window === "undefined") return true;
  if (isSupportPath(window.location.pathname)) return true;
  return false;
}

function pruneDedupCache(now: number) {
  for (const [key, ts] of recentKeys.entries()) {
    if (now - ts > DEDUP_WINDOW_MS) recentKeys.delete(key);
  }
}

function isRateLimited(now: number): boolean {
  if (now - minuteWindowStart > 60_000) {
    minuteWindowStart = now;
    reportsThisMinute = 0;
  }
  if (reportsThisMinute >= MAX_REPORTS_PER_MINUTE) return true;
  reportsThisMinute += 1;
  return false;
}

function dedupKey(payload: Pick<EventError, "source" | "message" | "stack">) {
  return `${payload.source}\0${payload.message}\0${payload.stack || ""}`;
}

function stringifyConsoleArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg instanceof Error) {
        return arg.stack || arg.message || String(arg);
      }
      if (typeof arg === "string") return arg;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(" ");
}

function errorFromReason(reason: unknown): {
  message: string;
  name?: string;
  stack?: string;
} {
  if (reason instanceof Error) {
    return {
      message: reason.message || String(reason),
      name: reason.name,
      stack: reason.stack,
    };
  }
  if (typeof reason === "string") {
    return { message: reason };
  }
  try {
    return { message: JSON.stringify(reason) };
  } catch {
    return { message: String(reason) };
  }
}

export interface RecordClientErrorInput {
  source: ClientErrorSource;
  message: string;
  name?: string;
  stack?: string;
  url?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  componentStack?: string;
}

export function recordClientError(input: RecordClientErrorInput): void {
  if (shouldSkipReporting()) return;

  const payload: EventError = {
    type: "error",
    message: input.message.slice(0, 4000),
    source: input.source,
    browserId: getBrowserId(),
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
    ...(input.name ? { name: input.name.slice(0, 200) } : {}),
    ...(input.stack ? { stack: input.stack.slice(0, 8000) } : {}),
    url: (input.url || window.location.href).slice(0, 2000),
    ...(input.filename ? { filename: input.filename.slice(0, 500) } : {}),
    ...(input.lineno != null ? { lineno: input.lineno } : {}),
    ...(input.colno != null ? { colno: input.colno } : {}),
    ...(input.componentStack
      ? { componentStack: input.componentStack.slice(0, 8000) }
      : {}),
  };

  const now = Date.now();
  pruneDedupCache(now);

  const key = dedupKey(payload);
  const lastSeen = recentKeys.get(key);
  if (lastSeen != null && now - lastSeen < DEDUP_WINDOW_MS) return;
  if (isRateLimited(now)) return;

  recentKeys.set(key, now);

  void recordEventWithCheck(payload).catch(() => {
    // Never surface reporting failures to the app.
  });
}

export function recordClientErrorFromReason(
  source: ClientErrorSource,
  reason: unknown,
  extra?: Partial<RecordClientErrorInput>
): void {
  const { message, name, stack } = errorFromReason(reason);
  recordClientError({
    source,
    message,
    name,
    stack,
    ...extra,
  });
}

export function recordClientErrorFromConsole(args: unknown[]): void {
  const firstError = args.find((arg): arg is Error => arg instanceof Error);
  recordClientError({
    source: "console.error",
    message: stringifyConsoleArgs(args).slice(0, 4000),
    name: firstError?.name,
    stack: firstError?.stack,
  });
}

export function installConsoleErrorReporter(): () => void {
  const original = console.error.bind(console);

  console.error = (...args: unknown[]) => {
    original(...args);
    if (!shouldSkipReporting()) {
      recordClientErrorFromConsole(args);
    }
  };

  return () => {
    console.error = original;
  };
}
